<template>
  <Layout title="CRM" description="Manage your customer relationships, track interactions, and improve client satisfaction.">
    <div>
      <div v-show="tab === 'customers'">
        <!-- Customer List and Add Form go here -->
        <div class="mb-4 d-flex justify-content-between align-items-center">
          <h2 class="h5 fw-semibold"  style="padding-left:24px">Customer List</h2>
          <div class="d-flex gap-2 ms-auto" style="margin-right: 10px;">
            <input
              v-model="searchQuery"
              type="text"
              class="form-control"
              placeholder="Search customers..."
            />
          </div>
          <button class="btn btn" style="background: #c77dff; color:white;" @click="showAdd = true">Add Client</button>
        </div>
        <div v-if="filteredCustomers.length ==0" style="padding-left:24px" >
          <p>Let's get started by adding your first awesome customer to the system! </p>
        </div>

        <div
            :class="filteredCustomers.length > 9 ? 'overflow-y-auto' : ''"
            style="max-height: 65vh; overflow-x: hidden; padding-left:24px" 
          >
            <div class="row g-3">
              <div
                class="col-md-6 col-lg-4"
                v-for="customer in filteredCustomers"
                :key="customer.id"
              >
                <div class="card h-100 border-0 shadow-sm rounded-4 p-3 hover-effect" @click="routeToClientDashboard(customer.id)">
                  <!-- Delete button -->
                    <button 
                      class="btn btn-sm text-danger position-absolute top-2 end-2 m-2 rounded-circle"
                      @click.stop="deleteCustomer(customer.id)"
                      title="Delete"
                    >
                      <Trash2 class="icon" :size="18" />
                    </button>
                  <div class="card-body">

                    <!-- Top Section: Logo + Name + Industry Badge -->
                    <div class="d-flex align-items-center mb-3 gap-3">
                      <!-- Logo -->
                      <div style="width: 50px; height: 50px; background: linear-gradient(135deg, #cfd9ff, #b1d3ff); border-radius: 50%;">
                        <div class="d-flex align-items-center justify-content-center h-100 fw-bold text-white">
                          {{ customer.initials }}
                        </div>
                      </div>

                      <!-- Name + Industry -->
                      <div class="flex-grow-1">
                        <h5 class="card-title fw-bold mb-0">{{ customer.name }}</h5>
                        <span class="badge rounded-pill bg-light border text-dark fw-medium mt-1">
                          {{ customer.industry }}
                        </span>
                        <span 
                          class="badge rounded-pill fw-medium mt-1 ml-2"
                          :class="{
                            'bg-danger': customer.leadScore.toLowerCase() === 'hot',
                            'bg-warning text-dark': customer.leadScore.toLowerCase() === 'warm',
                            'bg-info text-dark': customer.leadScore.toLowerCase() === 'cold',
                            'bg-light text-dark border': !customer.leadScore
                          }"
                        >
                          {{ customer.leadScore ? customer.leadScore.charAt(0).toUpperCase() + customer.leadScore.slice(1) : 'New!' }}
                        </span>


                      </div>

                      <!-- Optional star or button -->
                      <!-- <i class="bi bi-star"></i> -->
                    </div>

                    <!-- Contact Person -->
                    <p class="card-text text-dark small d-flex align-items-center mb-1">
                      <UserRound class="icon" :size="18" style="margin-right:5px"/> {{ customer.contact }}
                    </p>

                    <!-- Phone -->
                    <p class="card-text text-dark small d-flex align-items-center mb-1">
                      <NotebookTabs class="icon" :size="18" style="margin-right:5px"/> {{ customer.phone }}
                    </p>
                    
                    <!-- email -->
                    <p class="card-text text-dark small d-flex align-items-center mb-1">
                      <Mail class="icon" :size="18" style="margin-right:5px"/> {{ customer.email }}
                    </p>

                    <!-- website -->
                    <p class="card-text text-dark small d-flex align-items-center mb-1">
                      <Globe class="icon" :size="18" style="margin-right:5px"/> {{ customer.website }}
                    </p>

                    <!-- Projects -->
                    <!-- <div class="d-flex align-items-center gap-2 mt-2">
                      <span class="fw-medium">Projects:</span>
                      <span class="badge rounded-pill bg-info text-dark">
                        {{ customer.projects }}
                      </span>
                    </div> -->

                    <!-- Last Activity -->
                    <!-- <div class="text-muted small mt-2">
                      Last activity: {{ customer.lastActivity }}
                    </div> -->

                  </div>
                </div>


              </div>
            </div>
          </div>




        <!-- Add Customer Modal -->
        <div class="modal fade" tabindex="-1" :class="{ show: showAdd }" style="display: block" v-if="showAdd">
          <div class="modal-dialog">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">Add New Client</h5>
                <button type="button" class="btn-close" @click="showAdd = false"></button>
              </div>
              <div class="modal-body">
                <form @submit.prevent="submitCustomer">
                  <div class="row">
                    <div class="col-md-8 mb-3">
                      <label class="form-label">Company Name</label>
                      <input v-model="form.companyName" type="text" class="form-control" required />
                    </div>
                    <div class="col-md-4 mb-3">
                      <label class="form-label">Industry</label>
                      <input v-model="form.industry" type="text" class="form-control" required />
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Contact Person</label>
                      <input v-model="form.contactPerson" type="text" class="form-control" required />
                    </div>
                    <div class="col-md-4 mb-3">
                      <label class="form-label">Role/Position</label>
                      <select v-model="form.role" class="form-control" required>
                        <option value="Owner/C-Suite">Owner/C-Suite</option>
                        <option value="Manager/Team Lead">Manager/Team Lead</option>
                        <option value="Staff/Exec">Staff/Exec</option>
                        <option value="Others">Others</option>
                      </select>
                    </div>
                    <div class="col-md-2 mb-3">
                      <label class="form-label d-block">Referral</label>
                      <div class="form-check form-switch">
                        <input 
                          class="form-check-input" 
                          type="checkbox" 
                          id="referralSwitch" 
                          v-model="form.referral"
                        >
                        <label class="form-check-label" for="referralSwitch">
                          {{ form.referral ? "Yes" : "No" }}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div class="col- mb-3">
                      <label class="form-label">Contact No.</label>
                      <input v-model="form.phone" type="text" class="form-control" />
                    </div>
                  <div class="mb-3">
                    <label class="form-label">Email</label>
                    <input v-model="form.email" type="email" class="form-control" required />
                  </div>
                  <div class="mb-3">
                    <label class="form-label">Address</label>
                    <input v-model="form.address" type="text" class="form-control" />
                  </div>
                  <div class="mb-3">
                    <label class="form-label">Website</label>
                    <input v-model="form.website" type="text" class="form-control" />
                  </div>
                  
                  <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="showAdd = false">Cancel</button>
                    <button type="submit" class="btn btn-primary" :disabled="isAddingCustomer">
                      <span v-if="!isAddingCustomer">Add Customer</span>
                      <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        
        
      </div>

  
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/Layout.vue';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import router from "@/router";
import axios from "axios";
import { UserRound,NotebookTabs,Globe,Mail,Trash2  } from 'lucide-vue-next';

export default {
  name: 'CrmPage',
  components: { Layout,UserRound, NotebookTabs,Globe,Mail,Trash2   },
  data() {
    return {
      tab: 'customers',
      showAdd: false,
      showEdit: false, 
      // showAddLog:false,
      // showEditLog:false,
      // showEditMeeting:false,
      searchQuery: '',
      // logSearch: '',
      selectedCustomer:{},
      userID:"",
      token:"",
      // apiURL: "https://genstoryflow-backend-main-build-353838813175.europe-west1.run.app",
      // apiURL: "http://127.0.0.1:5001",
      apiURL: "https://genstoryflow-backend-main-build-353838813175.europe-west1.run.app",
      customerList: [],
      form: {
        companyName: '',
        email: '',
        phone: '',
        address: '',
        contactPerson:"",
        website:"", 
        industry:"",
        role:"",
        referral:""
      },
      isAddingCustomer: false,
    };
  },
  methods: {
    async getAuthHeader() {
      const user = getAuth().currentUser;
      if (!user) throw new Error('Not logged in');

      const token = await user.getIdToken(); // 🔐 always fresh
      return {
        Authorization: token
      };
    },
    routeToClientDashboard(clientID){
      console.log(clientID)
      this.$router.push({ name: 'ClientDashboard', params: { id: clientID } })
    },
    async submitCustomer() {
      const headers = await this.getAuthHeader();
      if (this.isAddingCustomer) return;
      this.isAddingCustomer = true;
      const payload = {
        userID: this.userID,
        CompanyName: this.form.companyName,
        Email: this.form.email,
        contact: this.form.phone,
        address: this.form.address,
        contactPerson: this.form.contactPerson, 
        website: this.form.website,
        industry: this.form.industry,
        profileID:this.userID,
        role:this.form.role,
        referral: this.form.referral
      };

      try {
        const res = await axios.post(this.apiURL + "/createCompany", payload, {headers});

        console.log("✅ Customer created:", res.data);
        // this.customerList.push(this.form); // update UI
    
        this.form =  {
        companyName: '',
        email: '',
        phone: '',
        address: '',
        contactPerson:"",
        website:"", 
        industry:"",
      }
      
      
      await this.loadCustomers();
          
        this.showAdd = false
      } catch (err) {
        console.error("❌ API error:", (err && err.response && err.response.data) || err.message);
      }finally {
        this.isAddingCustomer = false;
        this.showAdd = false
      }
    },
    async deleteCustomer(companyId) {

      try {
        const headers = await this.getAuthHeader();
        const res = await axios.delete(
          `${this.apiURL}/deleteCompany/${companyId}`,
          { headers }
        );


        if (res.data.code === 200) {
          // remove from list in UI
          this.customerList = this.customerList.filter(c => c.id !== companyId);
        } else {
          alert(res.data.message || "Delete failed");
        }
      } catch (err) {
        this.toast.error("An Error Occurred")
        console.error("❌ Delete API error:",  (err && err.response && err.response.data) ? err.response.data : err.message);
        // alert("Delete failed: " + (err.response?.data?.message || err.message));
      }
    },
    
    async checkAuthState() {
            const auth = getAuth();
            return new Promise((resolve, reject) => {
            onAuthStateChanged(auth, async (user) => {
                if (user) {
                try {
                    const idToken = await user.getIdToken();
                    this.token = idToken;
                    resolve(); // 🔥 Only resolve when token is ready
                } catch (error) {
                    reject(error);
                }
                } else {
                router.push("/");
                reject(new Error("No user logged in"));
                }
            });
            });
      },
      async loadCustomers() {
        try {
          const headers = await this.getAuthHeader();

          const res = await axios.get(`${this.apiURL}/getAllCompanies`, {
            params: { userID: this.userID },
            headers
          });

          this.customerList = res.data.data;
        } catch (err) {
          this.toast.error("Error Fetching")
          // console.error("❌ Failed to load customers:", err.message);
        }
      },

    
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '-';
      const date = new Date(dateTimeString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    
    formatToInputDate(dateStr) {
        const date = new Date(dateStr);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`; // MUST be yyyy-MM-dd
      },
    


  },
  computed: {
    logsWithCustomerName() {
      return (this.customerLogs || []).map(log => {
        const customer = this.customerList.find(c => c.id === log.customerId);
        return {
          ...log,
          customerName: customer ? customer.name : 'Unknown',
        };
      });
    },
    meetingsWithCustomerName() {
      return (this.meetingsList || []).map(meeting => {
        const customer = this.customerList.find(c => c.id === meeting.customerId);
        return {
          ...meeting,
          customerName: customer ? customer.name : 'Unknown'
        };
      });
    },
    filteredLogs() {
      const q = this.logSearch.toLowerCase().trim();
      return this.logsWithCustomerName.filter(
        log =>
          log.customerName.toLowerCase().includes(q) ||
          log.note.toLowerCase().includes(q)
      );
    },
    filteredCustomers() {
      const q = this.searchQuery.trim().toLowerCase();
      return this.customerList.filter(c =>
        c.name.toLowerCase().includes(q)
      );
    },
  },
  async mounted(){
      this.userID = this.$store.getters.getUserId;
      await this.checkAuthState();
      this.loadCustomers();
      // this.loadCustomerLogs();
      // this.fetchMeetingsByDate();
  }

};
</script>

<style scoped>
.custom-h3 {
  all: unset;
  display: block;
  font-weight: 700; /* same as fw-bold */
  font-size: 1.25rem; /* same as h3 */
  margin-bottom: 0.5rem; /* same as mb-2 */
}
.hover-effect {
  transition: all 0.3s ease-in-out;
}
.hover-effect:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
}
</style>
